import { DataTable } from "@/components/data-table";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { formatDate, formatNumber, formatString } from "@/shared/formats";
import { Venta } from "@/shared/types/venta";
import { Button, Stack, Typography, CircularProgress } from "@mui/material";
import { ReactNode, useState } from "react";
import PrintIcon from '@mui/icons-material/Print';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { getVentaKude } from "../api/get-venta-kude";
import { getVentaKudeTicketDatos } from "../api/get-venta-kude-ticket-datos";
import { openPdfFromBase64 } from "@/features/facturacion-electronica/utils/pdf-utils";
import { notify } from "@/shared/notification";
import { useCargarPresupuestoEnNuevaVenta } from "../hooks/use-cargar-presupuesto-en-nueva-venta";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import { usePrinter } from "@/components/print";
import KudeTicketView from "@/features/facturacion-electronica/components/kude-ticket-print/kude-ticket-view";

export default function VentaView({
    venta,
    onPrintButtonClick
}: {
    venta: Venta,
    onPrintButtonClick?: () => any
}) {
    const { monedaBase } = useMonetizacion();
    const { modeTicket, printPaperWidth } = useConfigurationContext();
    const cargarPresupuestoEnNuevaVenta = useCargarPresupuestoEnNuevaVenta();
    const printer = usePrinter();
    const [loadingKude, setLoadingKude] = useState(false);
    const [loadingPreload, setLoadingPreload] = useState(false);

    const handleCargarEnNuevaVenta = async () => {
        setLoadingPreload(true);
        try {
            await cargarPresupuestoEnNuevaVenta(venta);
        } finally {
            setLoadingPreload(false);
        }
    };

    const handlePrint = async () => {
        if (venta.tieneDE) {
            try {
                setLoadingKude(true);
                if (modeTicket) {
                    const response = await getVentaKudeTicketDatos(venta.id);
                    await printer.print({
                        documentTitle: `KUDE Venta N°${venta.numeroVenta}`,
                        printComponent: <KudeTicketView datos={response.data} />,
                        pageStyle: `@page { size: ${printPaperWidth} auto; margin: 0; }`
                    });
                } else {
                    const response = await getVentaKude(venta.id);
                    openPdfFromBase64(response.data.kudeBase64);
                }
                notify(`KUDE de la venta N°${venta.numeroVenta} abierto correctamente.`, { variant: "success" });
            } catch (error) {
                notify("Error al descargar el KUDE.", { variant: "error" });
                console.error(error);
            } finally {
                setLoadingKude(false);
            }
        } else {
            // Si no tiene DE, usar el comportamiento anterior
            onPrintButtonClick?.();
        }
    };

    const renderState = (venta: Venta) => {
        if (venta.anulado) return "Anulado";
        else if (venta.pagado) return "Pagado";
        else if (venta.presupuesto) return "Presupuesto";
        else return "Pendiente";
    }

    return <Stack gap={3} padding={3} flex={1}>
        <Stack gap={1}>
            <Typography variant="h6">Información General</Typography>
            <Stack direction={"row"} flexWrap={"wrap"} rowGap={1} columnGap={5}>
                <Stack gap={1}>
                    <AttributeValuePair label={"Fecha: "} value={formatDate(venta.fecha)} />
                    <AttributeValuePair label={"N° de Venta: "} value={formatString(venta.numeroVenta)} />
                    <AttributeValuePair label={"N° de Comprobante: "} value={formatString(venta.numComprobante)} />
                </Stack>
                <Stack gap={1}>
                    <AttributeValuePair label={"Cliente: "} value={formatString(venta.clienteNombre)} />
                    <AttributeValuePair label={"Vendedor: "} value={formatString(venta.vendedorNombre)} />
                    <AttributeValuePair label={"Estado: "} value={renderState(venta)} />
                </Stack>
            </Stack>
        </Stack>
        <Stack gap={2} flex={1}>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                <Typography variant="h6">Artículos</Typography>
                <Stack direction={"row"} gap={1}>
                    {venta.presupuesto && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={loadingPreload ? <CircularProgress size={20} /> : <ShoppingCartIcon />}
                            onClick={handleCargarEnNuevaVenta}
                            disabled={loadingPreload}
                        >
                            Cargar en Nueva Venta
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={loadingKude ? <CircularProgress size={20} /> : <PrintIcon />}
                        onClick={handlePrint}
                        disabled={loadingKude}
                    >
                        {venta.tieneDE ? "Imprimir KUDE" : "Imprimir"}
                    </Button>
                </Stack>
            </Stack>
            <DataTable flex={1}>
                <DataTable.Content
                    fit="flex"
                    columns={[
                        { header: "Código", render: i => i.articuloCodigo },
                        { header: "Artículo", render: i => i.articuloNombre },
                        { header: "Precio Unitario", render: i => formatNumber(i.precioUnitario), align: "right", width: "auto" },
                        { header: "Cantidad", render: i => formatNumber(i.cantidad), align: "right", width: "auto" },
                        { header: `Subtotal (${monedaBase.simbolo})`, render: i => formatNumber(i.total), align: "right", width: "auto" },
                        { header: `Utilidad (${monedaBase.simbolo})`, render: i => formatNumber(i.utilidad), align: "right", width: "auto" }
                    ]}
                    rows={venta.items}
                    footers={[
                        { render: () => <Typography variant="subtitle1">Total: </Typography>, span: 3 },
                        { render: () => <Typography variant="subtitle2">{formatNumber(venta.items.reduce((p, v) => p + v.cantidad, 0))}</Typography>, align: "right" },
                        { render: () => <Typography variant="subtitle2">{formatNumber(venta.items.reduce((p, v) => p + v.total, 0))}</Typography>, align: "right" },
                        { render: () => <Typography variant="subtitle2">{formatNumber(venta.items.reduce((p, v) => p + v.utilidad, 0))}</Typography>, align: "right" }
                    ]}
                />
            </DataTable>
        </Stack>
        <printer.Printer />
    </Stack>
}

function AttributeValuePair({
    label,
    value
}: {
    label: ReactNode,
    value: ReactNode
}) {
    return <Stack direction={"row"} gap={2} marginLeft={5}>
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="body2">{!value && value !== 0 ? "---" : value}</Typography>
    </Stack>
}
